import { useState, useEffect, useRef } from 'react';

interface BehaviorReflectionProps {
  className?: string;
}

interface BehaviorData {
  mouseSpeed: 'slow' | 'medium' | 'fast';
  scrollBehavior: 'patient' | 'scanning' | 'rushed';
  hoverDuration: number;
  hesitationCount: number;
  timeOnPage: number;
}

const BehaviorReflection = ({ className = '' }: BehaviorReflectionProps) => {
  const [reflection, setReflection] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0, time: Date.now() });
  const mouseSpeeds = useRef<number[]>([]);
  const scrollPositions = useRef<{ pos: number; time: number }[]>([]);
  const hoverStart = useRef<number | null>(null);
  const hesitationTimer = useRef<NodeJS.Timeout | null>(null);
  const hesitationCount = useRef(0);
  const pageLoadTime = useRef(Date.now());
  const reflectionShown = useRef(false);

  useEffect(() => {
    if (reflectionShown.current) return;

    // Track mouse speed
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      const dx = e.clientX - lastMousePos.current.x;
      const dy = e.clientY - lastMousePos.current.y;
      const dt = now - lastMousePos.current.time;
      
      if (dt > 0) {
        const speed = Math.sqrt(dx * dx + dy * dy) / dt;
        mouseSpeeds.current.push(speed);
        if (mouseSpeeds.current.length > 50) {
          mouseSpeeds.current = mouseSpeeds.current.slice(-50);
        }
      }
      
      lastMousePos.current = { x: e.clientX, y: e.clientY, time: now };

      // Detect hesitation (mouse stops moving)
      if (hesitationTimer.current) clearTimeout(hesitationTimer.current);
      hesitationTimer.current = setTimeout(() => {
        hesitationCount.current++;
      }, 2000);
    };

    // Track scroll behavior
    const handleScroll = () => {
      const now = Date.now();
      scrollPositions.current.push({ pos: window.scrollY, time: now });
      if (scrollPositions.current.length > 20) {
        scrollPositions.current = scrollPositions.current.slice(-20);
      }
    };

    // Track hover duration on interactive elements
    const handleMouseEnter = (e: MouseEvent) => {
      if ((e.target as HTMLElement).tagName === 'A' || 
          (e.target as HTMLElement).tagName === 'BUTTON') {
        hoverStart.current = Date.now();
      }
    };

    const handleMouseLeave = () => {
      hoverStart.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);

    // Generate reflection after 30 seconds
    const reflectionTimer = setTimeout(() => {
      if (reflectionShown.current) return;
      
      const behavior = analyzeBehavior();
      const message = generateReflection(behavior);
      
      if (message) {
        setReflection(message);
        setTimeout(() => setVisible(true), 100);
        reflectionShown.current = true;
        
        // Hide after 8 seconds
        setTimeout(() => {
          setVisible(false);
          setTimeout(() => setReflection(null), 500);
        }, 8000);
      }
    }, 30000);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
      if (hesitationTimer.current) clearTimeout(hesitationTimer.current);
      clearTimeout(reflectionTimer);
    };
  }, []);

  const analyzeBehavior = (): BehaviorData => {
    const avgSpeed = mouseSpeeds.current.length > 0
      ? mouseSpeeds.current.reduce((a, b) => a + b, 0) / mouseSpeeds.current.length
      : 0;

    let scrollBehavior: 'patient' | 'scanning' | 'rushed' = 'patient';
    if (scrollPositions.current.length >= 2) {
      const scrollSpeeds = [];
      for (let i = 1; i < scrollPositions.current.length; i++) {
        const dp = Math.abs(scrollPositions.current[i].pos - scrollPositions.current[i-1].pos);
        const dt = scrollPositions.current[i].time - scrollPositions.current[i-1].time;
        if (dt > 0) scrollSpeeds.push(dp / dt);
      }
      const avgScrollSpeed = scrollSpeeds.reduce((a, b) => a + b, 0) / scrollSpeeds.length;
      if (avgScrollSpeed > 1.5) scrollBehavior = 'rushed';
      else if (avgScrollSpeed > 0.5) scrollBehavior = 'scanning';
    }

    return {
      mouseSpeed: avgSpeed > 0.5 ? 'fast' : avgSpeed > 0.2 ? 'medium' : 'slow',
      scrollBehavior,
      hoverDuration: hoverStart.current ? Date.now() - hoverStart.current : 0,
      hesitationCount: hesitationCount.current,
      timeOnPage: (Date.now() - pageLoadTime.current) / 1000,
    };
  };

  const generateReflection = (behavior: BehaviorData): string | null => {
    const reflections: string[] = [];

    if (behavior.hesitationCount > 3) {
      reflections.push("You paused several times.");
    }
    
    if (behavior.mouseSpeed === 'slow') {
      reflections.push("Your movements were deliberate.");
    }
    
    if (behavior.scrollBehavior === 'rushed') {
      reflections.push("You moved quickly through this.");
    } else if (behavior.scrollBehavior === 'patient') {
      reflections.push("You took your time here.");
    }

    if (behavior.timeOnPage > 60) {
      reflections.push("You've been here a while.");
    }

    if (reflections.length === 0) return null;
    
    return reflections[Math.floor(Math.random() * reflections.length)];
  };

  if (!reflection) return null;

  return (
    <div 
      className={`fixed bottom-8 left-8 text-xs text-muted-foreground/40 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      } ${className}`}
    >
      <span className="italic">{reflection}</span>
    </div>
  );
};

export default BehaviorReflection;
